/**
 * Utilities for finding product grid tiles in the DOM.
 * Used by scroll restoration to wait for a specific tile to appear
 * before scrolling, preventing footer flash on POP navigation.
 *
 * Tiles are identified by the `data-product-tile` attribute on their
 * Link elements (set in DirectoryProductTile and DirectoryFeaturedTile).
 */

const GRID_CONTAINER_SELECTOR = '.products-page-custom-filters';
const TILE_SELECTOR = '[data-product-tile]';

/**
 * Find a product grid tile in the DOM by its product link.
 * Scoped to the grid container to avoid matching links in other
 * parts of the page (header, footer, recently viewed, etc.).
 *
 * @param productLink - The product's relative URL path
 * @returns The anchor element if found, null otherwise
 */
function findGridTile(productLink: string): HTMLElement | null {
    if (!productLink) return null;
    const container = document.querySelector(GRID_CONTAINER_SELECTOR);
    if (!container) return null;
    const anchor = container.querySelector(
        `${TILE_SELECTOR}[href="${CSS.escape(productLink)}"]`,
    );
    return anchor as HTMLElement | null;
}

/**
 * Find the first visible grid tile in the viewport.
 * Only matches elements with `data-product-tile` to avoid false
 * positives from breadcrumbs, category links, or featured content.
 *
 * Used when the user navigates away without clicking a tile —
 * saves the topmost visible tile so we can scroll back to it.
 */
export function findFirstVisibleGridTile(): string | null {
    const container = document.querySelector(GRID_CONTAINER_SELECTOR);
    if (!container) return null;

    const tiles = container.querySelectorAll<HTMLAnchorElement>(TILE_SELECTOR);
    if (tiles.length === 0) return null;

    // First pass: find the first tile whose top edge is visible in viewport
    for (const tile of tiles) {
        const rect = tile.getBoundingClientRect();
        if (rect.top >= 0 && rect.top < window.innerHeight) {
            return tile.getAttribute('href');
        }
    }

    // Second pass: find the tile closest to viewport top that's partially above
    // (user scrolled partway past a row)
    let bestMatch: { href: string; top: number } | null = null;
    for (const tile of tiles) {
        const rect = tile.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < 0) {
            if (!bestMatch || rect.top > bestMatch.top) {
                const href = tile.getAttribute('href');
                if (href) bestMatch = { href, top: rect.top };
            }
        }
    }

    return bestMatch?.href ?? null;
}

/**
 * Poll for a tile to appear in the DOM, then scroll to the saved position.
 * The tile is used as a DOM-readiness signal — its presence means the grid
 * has rendered. We then use the saved scrollY for exact positioning (rather
 * than scrollIntoView which pins to viewport top).
 *
 * @param tileLink - Product link to poll for
 * @param scrollY - Saved scroll position to restore to
 * @param onComplete - Called after scroll is restored (or timeout)
 * @returns Cleanup function to cancel polling
 */
export function pollForTileAndScroll(
    tileLink: string,
    scrollY: number,
    onComplete: () => void,
): () => void {
    let rafId: number | undefined;
    let attempts = 0;
    const MAX_ATTEMPTS = 150; // ~2.5s at 60fps

    const poll = () => {
        attempts++;
        const tile = findGridTile(tileLink);
        if (tile) {
            // Tile found = grid has rendered. Use saved scrollY for exact position.
            window.scrollTo({ top: scrollY, behavior: 'instant' });
            onComplete();
        } else if (attempts < MAX_ATTEMPTS) {
            rafId = requestAnimationFrame(poll);
        } else {
            // Safety timeout: fall back to scrollY anyway
            window.scrollTo({ top: scrollY, behavior: 'instant' });
            onComplete();
        }
    };
    rafId = requestAnimationFrame(poll);

    return () => {
        if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
}
